import { FC } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { FranchiseeBundleAddOnRow } from '@/types/namedPrice.types';
import { StoreGroupWithCount } from './StoreGroupPricesTable';
import styles from './BundleAddOnConfirmation.module.css';

export interface BundleAddOnConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  addOn: FranchiseeBundleAddOnRow | null;
  storeGroups: StoreGroupWithCount[];
  newPrice: string;
  onSubmit: () => void;
}

const BundleAddOnConfirmation: FC<BundleAddOnConfirmationProps> = ({
  isOpen,
  onClose,
  onCancel,
  addOn,
  storeGroups,
  newPrice,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const handleSubmit = () => {
    onSubmit();
    onClose();
  };

  const handleCloseDrawer = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const formattedNewPrice = `$${parseFloat(newPrice || '0').toFixed(2)}`;

  if (!addOn) return null;

  return (
    <SwipeableDrawer open={isOpen} onClose={handleCloseDrawer} onOpen={() => undefined} anchor="bottom">
      <div className={styles.container}>
        <header>
          <div>
            <h1>{t('editBundleAddOn.title')}</h1>
          </div>
          <button type="button" onClick={handleCloseDrawer} aria-label="Close">
            <X data-testid="bundle-add-on-confirmation-close" size={24} color="var(--black-900)" />
          </button>
        </header>

        <div className={styles.content}>
          <div className={styles.confirmationContent}>
            <h2 className={styles.confirmationTitle}>{t('editMenuPrice.submitMenuChanges')}</h2>

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.productHeader}>{t('franchiseeBundleAddOns.bundleAddOnColumn')}</th>
                    {storeGroups.map(({ storeGroup, selected, total }) => (
                      <th key={storeGroup} className={styles.priceHeader}>
                        <div className={styles.headerWithCount}>
                          <div className={styles.headerName}>{storeGroup.toUpperCase()}</div>
                          <div className={styles.headerCount}>
                            {selected} / {total}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={styles.productCell}>
                      <div className={styles.productName}>{addOn.name}</div>
                    </td>
                    {storeGroups.map(({ storeGroup }) => (
                      <td key={storeGroup} className={styles.priceCell}>
                        {formattedNewPrice}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.alertContainer}>
              <CalendarAlert requestedVerbiage />
            </div>

            <div className={styles.buttonContainer}>
              <Button
                className={styles.cancelButton}
                onClick={handleCancel}
                variant={ButtonVariants.Secondary}
                type="button"
              >
                {t('editMenuPrice.cancel')}
              </Button>
              <Button
                className={styles.submitButton}
                onClick={handleSubmit}
                variant={ButtonVariants.Primary}
                type="button"
                data-testid="bundle-add-on-confirmation-submit"
              >
                {t('editMenuPrice.submitChanges')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
};

export default BundleAddOnConfirmation;
